import React, { ReactElement, useEffect, useState } from 'react';
import JsPDF from 'jspdf';
import QRCode from 'qrcode';
import dayjs from 'dayjs';
import { useParams, useSearchParams } from 'react-router-dom';
import { Col, Row, Result, Popover, Space } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { PrinterOutlined, PaperClipOutlined } from '@ant-design/icons';
import styles from './memberCertificate.module.css';
import { getUserCertificate } from '../../services/services';
import download from '../../assets/download.png';
import { CompanyInterface } from '../../models/companyInterface';
import config from '../../config';
import { UserCategory } from '../../consts/UserCategoryEnum';
import plaCertificateBack from '../../assets/plaCertBack.png';
import { ReactComponent as Logo } from '../../assets/plaLogo.svg';
import { ReactComponent as Signature } from '../../assets/plaSignature.svg';

const MemberCertificatePLA = (): ReactElement => {
  const { t } = useTranslation('common');
  const [qr, setQr] = useState<string | null>(null);
  const [companyData, setCompanyData] = useState<CompanyInterface>();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');

  useEffect(() => {
    const setCompany = async (id: string): Promise<void> => {
      const userCertificate = await getUserCertificate(id);
      if (userCertificate === undefined) return;
      if (userCertificate?.registrationDate !== undefined)
        userCertificate.registrationDate = dayjs(
          userCertificate.registrationDate,
          config.dateFormat.universalFormat,
        ).format(config.dateFormat.certificate);
      if (userCertificate?.membershipDue !== undefined)
        userCertificate.membershipDue = dayjs(
          userCertificate.membershipDue,
          config.dateFormat.universalFormat,
        ).format(config.dateFormat.certificate);
      setCompanyData(userCertificate);
    };
    if (id !== undefined) setCompany(id);
  }, [id]);

  const handleDownload = async (scale: number): Promise<void> => {
    const doc = new JsPDF('l', 'mm', 'a4');
    const elementHTML = document.querySelector('#pdfContent') as HTMLElement;

    const pdfWidth = 297; // A4 width in mm
    const canvas = await html2canvas(elementHTML, {
      scale,
      useCORS: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const contentWidth = canvas.width;
    const contentHeight = canvas.height;
    const ratio = contentHeight / contentWidth;

    const a4Height = pdfWidth * ratio;

    doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, a4Height);
    doc.save('member-certificate.pdf');
  };

  useEffect(() => {
    QRCode.toDataURL(
      `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }${config.routes.memberCertificate.replace(':id', id as string)}?status=1`,
    )
      .then((url) => {
        setQr(url);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [id]);

  const isValid =
    companyData &&
    dayjs(companyData?.membershipDue, config.dateFormat.certificate).format('YYYY.MM.DD') >=
      dayjs().format('YYYY.MM.DD');

  return (
    <div className={styles.container}>
      {status === '1' ? (
        companyData && (
          <Result
            status={isValid ? 'success' : 'warning'}
            title={t(`misc.${isValid ? 'valid' : 'notValid'}CertificateMessage`)
              .replace(':company', companyData?.companyName)
              .replace(
                ':date',
                dayjs(companyData?.membershipDue, config.dateFormat.certificate).format(
                  'YYYY.MM.DD',
                ),
              )}
          />
        )
      ) : (
        <>
          <Row justify={'end'} className={styles.downloadBtnWrap}>
            <Col>
              <Popover
                content={
                  <Space direction='vertical' className={styles.popoverDownload}>
                    <button
                      onClick={() => {
                        handleDownload(2);
                      }}
                      className={styles.download}
                    >
                      <Row justify={'space-around'} align='middle' gutter={5}>
                        <Col>
                          <PaperClipOutlined />
                        </Col>
                        <Col>{t('misc.webQuality')}</Col>
                      </Row>
                    </button>
                    <button
                      onClick={() => {
                        handleDownload(4);
                      }}
                      className={styles.download}
                    >
                      <Row justify={'space-around'} align='middle' gutter={5}>
                        <Col>
                          <PrinterOutlined />
                        </Col>
                        <Col>{t('misc.printQuality')}</Col>
                      </Row>
                    </button>
                  </Space>
                }
                trigger='click'
                placement='bottomRight'
              >
                <button className={styles.download}>
                  <Row justify={'space-around'} align='middle' gutter={5}>
                    <Col>
                      <img src={download} className={styles.downloadImg} alt='' />
                    </Col>
                    <Col>{t('misc.downloadCertificate')}</Col>
                  </Row>
                </button>
              </Popover>
            </Col>
          </Row>
          <div className={styles.pdfContent} id='pdfContent'>
            <div className={styles.content}>
              <Row justify='space-between'>
                <Col className={styles.title}>
                  <Logo className={styles.logo} />
                  {companyData?.userCategory === UserCategory.MEMBER
                    ? t('misc.membership').toUpperCase()
                    : companyData?.userCategory === UserCategory.PARTNER &&
                      t('misc.partnership').toUpperCase()}{' '}
                  {t('misc.certificate').toUpperCase()}
                </Col>
              </Row>
              <Row>
                <Col span={24} className={styles.text}>
                  {companyData?.userCategory === UserCategory.MEMBER && (
                    <Trans
                      i18nKey='common:certificatePLA.memberDescription'
                      components={[
                        <div className={styles.companyName} key={0} />,
                        <b key={1} />,
                        <b key={2} />,
                      ]}
                      values={{
                        companyName: companyData?.companyName,
                        country: companyData?.country,
                      }}
                    />
                  )}
                  {companyData?.userCategory === UserCategory.PARTNER && (
                    <Trans
                      i18nKey='common:certificatePLA.partnerDescription'
                      components={[
                        <div className={styles.companyName} key={0} />,
                        <b key={1} />,
                        <b key={2} />,
                      ]}
                      values={{
                        companyName: companyData?.companyName,
                        in: companyData?.partnerOtherCategory ? ` ${t('misc.in')} ` : '',
                        serviceName: companyData?.partnerOtherCategory ?? '',
                      }}
                    />
                  )}

                  <Row className={styles.period} justify='space-between'>
                    <Col className={styles.dateText}>
                      {t('misc.issueDate')}:
                      <br />
                      <b>{companyData?.registrationDate as string}</b>
                    </Col>
                    <Col className={styles.dateText}>
                      {t('misc.validTill')}:
                      <br />
                      <b>{companyData?.membershipDue as string}</b>
                    </Col>
                  </Row>
                  <Row className={styles.signatureWrapper} justify='space-between'>
                    <Col>
                      <Row justify='space-between' align={'middle'}>
                        <Col>
                          <Signature className={styles.signature} />
                        </Col>
                        <Col className={styles.signatureText}>
                          <span className={styles.ceoName}>{t('certificatePLA.part11')}</span>
                          <br />
                          {t('certificatePLA.part12')}
                          <br />
                          {t('certificatePLA.part13')}
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <Row className={styles.qr} justify='space-between' align={'middle'}>
                        <Col>
                          {qr !== null && <img src={qr} className={styles.qrImg} />}
                          <div className={styles.qrText}>{t('certificatePLA.part10')}</div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
            <div className={styles.footerWrapper}></div>
            <img
              className={styles.background}
              src={plaCertificateBack}
              alt='certificate-background'
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MemberCertificatePLA;
